import React, { useState, useEffect } from 'react';

const API = 'https://api.glowytics.com';
const fmtMoney = (n, currency = 'TRY') => {
  if (n === null || n === undefined) return '—';
  return Number(n).toLocaleString('en-US', { style: 'currency', currency, minimumFractionDigits: 2 });
};
const fmtNum = (n) => Number(n || 0).toLocaleString('en-US');
const fmtPct = (n) => Number(n || 0).toFixed(2) + '%';

const statusColors = {
  Active: 'bg-green-100 text-green-800',
  Disabled: 'bg-red-100 text-red-800',
  Unsettled: 'bg-yellow-100 text-yellow-800',
  'Pending Risk Review': 'bg-orange-100 text-orange-800',
  'Pending Settlement': 'bg-orange-100 text-orange-800',
  'In Grace Period': 'bg-yellow-100 text-yellow-800',
};


const monthLabel = (dateStr) => {
  if (!dateStr) return '';
  const d = new Date(dateStr + 'T00:00:00');
  return d.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
};

const dayLabel = (dateStr) => {
  if (!dateStr) return '';
  const d = new Date(dateStr + 'T00:00:00');
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};


export default function MetaBilling() {
  const brandId = typeof window !== 'undefined' ? localStorage.getItem('currentBrandId') : null;
  const [data, setData] = useState(null);
  const [spendData, setSpendData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [spendLoading, setSpendLoading] = useState(true);
  const [error, setError] = useState(null);


  useEffect(() => {
    if (!brandId) return;
    setLoading(true);
    setSpendLoading(true);
    setError(null);

    // Fetch billing info and spend data in parallel
    Promise.all([
      fetch(`${API}/api/meta-ads/billing`, {
        headers: { 'X-Brand-ID': brandId },
        credentials: 'include'
      }).then(r => r.json()),
      fetch(`${API}/api/meta-ads/billing/spend?months=6`, {
        headers: { 'X-Brand-ID': brandId },
        credentials: 'include'
      }).then(r => r.json()),
    ]).then(([billingResp, spendResp]) => {
      if (billingResp.error) setError(billingResp.error);
      else setData(billingResp);
      if (!spendResp.error) setSpendData(spendResp);
    }).catch(e => setError(e.message))
      .finally(() => { setLoading(false); setSpendLoading(false); });
  }, [brandId]);


  if (!brandId) return <div className="p-6 text-gray-500">Please select a brand first.</div>;

  return (
    <div className="p-4 md:p-6 max-w-7xl mx-auto space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-xl font-bold text-gray-900">Meta Ads — Billing Overview</h1>
        <p className="text-sm text-gray-500 mt-0.5">Ad account billing, monthly spend breakdown, and daily spend tracking</p>
      </div>

      {loading && (
        <div className="flex items-center justify-center py-20">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      )}

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-red-700 text-sm">{error}</div>
      )}

      {data && !loading && (
        <>
          {/* Summary Cards */}
          {data.accounts?.length > 0 && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="bg-white border rounded-xl p-4 text-center">
                <div className="text-xs font-semibold text-gray-400 uppercase tracking-wide">Total Accounts</div>
                <div className="text-3xl font-extrabold mt-1 text-gray-900">{data.accounts.length}</div>
              </div>
              <div className="bg-white border rounded-xl p-4 text-center">
                <div className="text-xs font-semibold text-gray-400 uppercase tracking-wide">Active</div>
                <div className="text-3xl font-extrabold mt-1 text-green-600">
                  {data.accounts.filter(a => a.accountStatus === 'Active').length}
                </div>
              </div>
              <div className="bg-white border rounded-xl p-4 text-center">
                <div className="text-xs font-semibold text-gray-400 uppercase tracking-wide">Total Amount Spent</div>
                <div className="text-2xl font-extrabold mt-1 text-gray-900">
                  {fmtMoney(data.accounts.reduce((s, a) => s + (a.amountSpent || 0), 0), data.accounts[0]?.currency || 'TRY')}
                </div>
              </div>
              <div className="bg-white border rounded-xl p-4 text-center">
                <div className="text-xs font-semibold text-gray-400 uppercase tracking-wide">Total Balance</div>
                <div className="text-2xl font-extrabold mt-1 text-blue-600">
                  {fmtMoney(data.accounts.reduce((s, a) => s + (a.balance || 0), 0), data.accounts[0]?.currency || 'TRY')}
                </div>
              </div>
            </div>
          )}

          {/* Account Cards */}
          <div className="space-y-4">
            {data.accounts?.map((acc, i) => {
              const spendAcc = spendData?.accounts?.find(s => s.accountId === acc.accountId);
              const currency = acc.currency || 'TRY';
              const fs = acc.fundingSourceDetails;
              const typeMap = { 1: 'Credit Card', 2: 'PayPal', 3: 'Coupon', 4: 'Direct Debit', 5: 'Store Credit', 6: 'Facebook Wallet' };

              return (
                <div key={acc.connectionId || i} className="bg-white border rounded-xl overflow-hidden">
                  {/* Account Header */}
                  <div className="px-6 py-4 border-b bg-gray-50 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-blue-600 text-white flex items-center justify-center font-bold text-sm">
                        {(acc.accountName || 'M')[0].toUpperCase()}
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <h2 className="font-bold text-gray-900">{acc.accountName}</h2>
                          {acc.isDefault && (
                            <span className="text-[10px] bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded font-semibold uppercase">Default</span>
                          )}
                        </div>
                        <p className="text-xs text-gray-400">
                          ID: {acc.accountId}
                          {acc.businessName && <span className="ml-2">| {acc.businessName}</span>}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {/* Payment Method Badge */}
                      {fs?.display_string && (
                        <span className="px-2.5 py-1 rounded-full text-xs font-semibold bg-gray-800 text-white">
                          {fs.display_string}
                        </span>
                      )}
                      {acc.accountStatus && (
                        <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${statusColors[acc.accountStatus] || 'bg-gray-100 text-gray-600'}`}>
                          {acc.accountStatus}
                        </span>
                      )}
                      {acc.currency && (
                        <span className="px-2.5 py-1 rounded-full text-xs font-semibold bg-gray-100 text-gray-600">
                          {acc.currency}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Error State */}
                  {acc.error && (
                    <div className="p-4 bg-red-50 text-red-600 text-sm">
                      <span className="font-semibold">Error:</span> {acc.error}
                    </div>
                  )}

                  {/* Billing Details */}
                  {!acc.error && (
                    <div className="p-6 space-y-6">
                      {/* KPI Row */}
                      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                        <div className="rounded-xl border p-4">
                          <p className="text-xs text-gray-400 uppercase tracking-wide font-semibold">Total Spent</p>
                          <p className="text-xl font-extrabold text-gray-900 mt-1">{fmtMoney(acc.amountSpent, currency)}</p>
                          <p className="text-[10px] text-gray-400 mt-1">Lifetime</p>
                        </div>
                        <div className="rounded-xl border p-4">
                          <p className="text-xs text-gray-400 uppercase tracking-wide font-semibold">Balance</p>
                          <p className={`text-xl font-extrabold mt-1 ${acc.balance > 0 ? 'text-blue-600' : 'text-gray-400'}`}>
                            {fmtMoney(acc.balance, currency)}
                          </p>
                          <p className="text-[10px] text-gray-400 mt-1">Prepaid balance</p>
                        </div>
                        <div className="rounded-xl border p-4">
                          <p className="text-xs text-gray-400 uppercase tracking-wide font-semibold">Spend Cap</p>
                          <p className={`text-xl font-extrabold mt-1 ${acc.spendCap > 0 ? 'text-orange-600' : 'text-gray-400'}`}>
                            {acc.spendCap > 0 ? fmtMoney(acc.spendCap, currency) : 'No Limit'}
                          </p>
                          {acc.spendCap > 0 && (
                            <div className="mt-1.5 w-full bg-gray-100 rounded-full h-1.5">
                              <div
                                className={`h-1.5 rounded-full ${(acc.amountSpent / acc.spendCap) > 0.9 ? 'bg-red-500' : 'bg-blue-500'}`}
                                style={{ width: `${Math.min((acc.amountSpent / acc.spendCap) * 100, 100)}%` }}
                              />
                            </div>
                          )}
                        </div>
                        <div className="rounded-xl border p-4">
                          <p className="text-xs text-gray-400 uppercase tracking-wide font-semibold">Payment</p>
                          <p className="text-xl font-extrabold text-gray-900 mt-1">{fs?.display_string || '—'}</p>
                          <p className="text-[10px] text-gray-400 mt-1">{fs ? (typeMap[fs.type] || '') : ''}</p>
                        </div>
                        <div className="rounded-xl border p-4">
                          <p className="text-xs text-gray-400 uppercase tracking-wide font-semibold">Timezone</p>
                          <p className="text-sm font-bold text-gray-900 mt-1">{acc.timezone || '—'}</p>
                          <p className="text-[10px] text-gray-400 mt-1">Min daily: {acc.minDailyBudget > 0 ? fmtMoney(acc.minDailyBudget, currency) : '—'}</p>
                        </div>
                      </div>

                      {/* Monthly Spend Table */}
                      {spendAcc?.monthly?.length > 0 && (
                        <div>
                          <h3 className="text-sm font-bold text-gray-900 mb-3 uppercase tracking-wider">Monthly Spend Breakdown</h3>
                          <div className="overflow-x-auto rounded-xl border">
                            <table className="w-full text-sm">
                              <thead>
                                <tr className="bg-gray-50 text-gray-500 text-xs uppercase tracking-wider">
                                  <th className="py-3 px-4 text-left font-semibold">Month</th>
                                  <th className="py-3 px-4 text-right font-semibold">Spend</th>
                                  <th className="py-3 px-4 text-right font-semibold">Impressions</th>
                                  <th className="py-3 px-4 text-right font-semibold">Clicks</th>
                                  <th className="py-3 px-4 text-right font-semibold">CPC</th>
                                  <th className="py-3 px-4 text-right font-semibold">CPM</th>
                                  <th className="py-3 px-4 text-right font-semibold">CTR</th>
                                  <th className="py-3 px-4 text-right font-semibold">Purchases</th>
                                  <th className="py-3 px-4 text-right font-semibold">Purchase Value</th>
                                  <th className="py-3 px-4 text-right font-semibold">ROAS</th>
                                  <th className="py-3 px-4 text-right font-semibold">Add to Cart</th>
                                  <th className="py-3 px-4 text-right font-semibold">Checkout</th>
                                </tr>
                              </thead>
                              <tbody className="divide-y divide-gray-100">
                                {spendAcc.monthly.map((row, ri) => {
                                  const roas = row.spend > 0 ? (row.purchaseValue / row.spend) : 0;
                                  return (
                                    <tr key={ri} className="hover:bg-gray-50">
                                      <td className="py-2.5 px-4 font-medium text-gray-900 whitespace-nowrap">{monthLabel(row.dateStart)}</td>
                                      <td className="py-2.5 px-4 text-right font-bold text-gray-900">{fmtMoney(row.spend, currency)}</td>
                                      <td className="py-2.5 px-4 text-right text-gray-600">{fmtNum(row.impressions)}</td>
                                      <td className="py-2.5 px-4 text-right text-gray-600">{fmtNum(row.clicks)}</td>
                                      <td className="py-2.5 px-4 text-right text-gray-500">{fmtMoney(row.cpc, currency)}</td>
                                      <td className="py-2.5 px-4 text-right text-gray-500">{fmtMoney(row.cpm, currency)}</td>
                                      <td className="py-2.5 px-4 text-right text-gray-500">{fmtPct(row.ctr)}</td>
                                      <td className="py-2.5 px-4 text-right font-semibold text-green-700">{fmtNum(row.purchases)}</td>
                                      <td className="py-2.5 px-4 text-right font-semibold text-green-700">{row.purchaseValue > 0 ? fmtMoney(row.purchaseValue, currency) : '—'}</td>
                                      <td className={`py-2.5 px-4 text-right font-bold ${roas >= 3 ? 'text-green-700' : roas >= 1 ? 'text-orange-600' : 'text-red-600'}`}>
                                        {roas > 0 ? roas.toFixed(2) + 'x' : '—'}
                                      </td>
                                      <td className="py-2.5 px-4 text-right text-gray-500">{fmtNum(row.addToCart)}</td>
                                      <td className="py-2.5 px-4 text-right text-gray-500">{fmtNum(row.initiatedCheckout)}</td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                              <tfoot>
                                <tr className="bg-gray-50 border-t-2 border-gray-200">
                                  <td className="py-3 px-4 font-bold text-gray-900">TOTAL</td>
                                  <td className="py-3 px-4 text-right font-bold text-gray-900">{fmtMoney(spendAcc.monthly.reduce((s, r) => s + r.spend, 0), currency)}</td>
                                  <td className="py-3 px-4 text-right font-semibold text-gray-700">{fmtNum(spendAcc.monthly.reduce((s, r) => s + r.impressions, 0))}</td>
                                  <td className="py-3 px-4 text-right font-semibold text-gray-700">{fmtNum(spendAcc.monthly.reduce((s, r) => s + r.clicks, 0))}</td>
                                  <td className="py-3 px-4 text-right text-gray-500">—</td>
                                  <td className="py-3 px-4 text-right text-gray-500">—</td>
                                  <td className="py-3 px-4 text-right text-gray-500">—</td>
                                  <td className="py-3 px-4 text-right font-bold text-green-700">{fmtNum(spendAcc.monthly.reduce((s, r) => s + r.purchases, 0))}</td>
                                  <td className="py-3 px-4 text-right font-bold text-green-700">{fmtMoney(spendAcc.monthly.reduce((s, r) => s + r.purchaseValue, 0), currency)}</td>
                                  {(() => {
                                    const tSpend = spendAcc.monthly.reduce((s, r) => s + r.spend, 0);
                                    const tPV = spendAcc.monthly.reduce((s, r) => s + r.purchaseValue, 0);
                                    const tRoas = tSpend > 0 ? tPV / tSpend : 0;
                                    return <td className={`py-3 px-4 text-right font-bold ${tRoas >= 3 ? 'text-green-700' : tRoas >= 1 ? 'text-orange-600' : 'text-red-600'}`}>{tRoas > 0 ? tRoas.toFixed(2) + 'x' : '—'}</td>;
                                  })()}
                                  <td className="py-3 px-4 text-right font-semibold text-gray-700">{fmtNum(spendAcc.monthly.reduce((s, r) => s + r.addToCart, 0))}</td>
                                  <td className="py-3 px-4 text-right font-semibold text-gray-700">{fmtNum(spendAcc.monthly.reduce((s, r) => s + r.initiatedCheckout, 0))}</td>
                                </tr>
                              </tfoot>
                            </table>
                          </div>
                        </div>
                      )}

                      {/* Daily Spend - Current Month */}
                      {spendAcc?.daily?.length > 0 && (
                        <div>
                          <h3 className="text-sm font-bold text-gray-900 mb-3 uppercase tracking-wider">
                            Daily Spend — {new Date().toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
                          </h3>
                          {/* Bar Chart */}
                          <div className="bg-gray-50 rounded-xl border p-4 mb-3">
                            <div className="flex items-end gap-1" style={{ height: 120 }}>
                              {(() => {
                                const maxSpend = Math.max(...spendAcc.daily.map(d => d.spend), 1);
                                return spendAcc.daily.map((d, di) => {
                                  const h = (d.spend / maxSpend) * 100;
                                  return (
                                    <div key={di} className="flex-1 flex flex-col items-center gap-1 group relative">
                                      <div className="absolute -top-6 bg-gray-900 text-white text-[9px] px-1.5 py-0.5 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-10">
                                        {fmtMoney(d.spend, currency)}
                                      </div>
                                      <div
                                        className="w-full bg-blue-500 rounded-t hover:bg-blue-600 transition-colors cursor-default"
                                        style={{ height: `${Math.max(h, 2)}%` }}
                                      />
                                      <span className="text-[9px] text-gray-400">{new Date(d.date + 'T00:00:00').getDate()}</span>
                                    </div>
                                  );
                                });
                              })()}
                            </div>
                          </div>

                          {/* Daily Table */}
                          <div className="overflow-x-auto rounded-xl border">
                            <table className="w-full text-sm">
                              <thead>
                                <tr className="bg-gray-50 text-gray-500 text-xs uppercase tracking-wider">
                                  <th className="py-2.5 px-4 text-left font-semibold">Date</th>
                                  <th className="py-2.5 px-4 text-right font-semibold">Spend</th>
                                  <th className="py-2.5 px-4 text-right font-semibold">Impressions</th>
                                  <th className="py-2.5 px-4 text-right font-semibold">Clicks</th>
                                </tr>
                              </thead>
                              <tbody className="divide-y divide-gray-100">
                                {spendAcc.daily.map((d, di) => (
                                  <tr key={di} className="hover:bg-gray-50">
                                    <td className="py-2 px-4 font-medium text-gray-900 whitespace-nowrap">{dayLabel(d.date)}</td>
                                    <td className="py-2 px-4 text-right font-bold text-gray-900">{fmtMoney(d.spend, currency)}</td>
                                    <td className="py-2 px-4 text-right text-gray-600">{fmtNum(d.impressions)}</td>
                                    <td className="py-2 px-4 text-right text-gray-600">{fmtNum(d.clicks)}</td>
                                  </tr>
                                ))}
                              </tbody>
                              <tfoot>
                                <tr className="bg-gray-50 border-t-2">
                                  <td className="py-2.5 px-4 font-bold text-gray-900">TOTAL</td>
                                  <td className="py-2.5 px-4 text-right font-bold text-gray-900">{fmtMoney(spendAcc.daily.reduce((s, d) => s + d.spend, 0), currency)}</td>
                                  <td className="py-2.5 px-4 text-right font-semibold text-gray-700">{fmtNum(spendAcc.daily.reduce((s, d) => s + d.impressions, 0))}</td>
                                  <td className="py-2.5 px-4 text-right font-semibold text-gray-700">{fmtNum(spendAcc.daily.reduce((s, d) => s + d.clicks, 0))}</td>
                                </tr>
                              </tfoot>
                            </table>
                          </div>
                        </div>
                      )}

                      {/* Spend Loading */}
                      {spendLoading && !spendAcc && (
                        <div className="flex items-center justify-center py-8">
                          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500"></div>
                          <span className="ml-2 text-sm text-gray-400">Loading spend data...</span>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* No Accounts */}
          {(!data.accounts || data.accounts.length === 0) && (
            <div className="bg-gray-50 border rounded-xl p-8 text-center">
              <p className="text-gray-500">No Meta ad accounts found for this brand.</p>
              <p className="text-xs text-gray-400 mt-1">Connect a Meta ad account first from Settings.</p>
            </div>
          )}
        </>
      )}
    </div>
  );
}
